import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import './FilterModal.css';
import '../../ApplicationsDashboard/ApplicationsDashboard.css';
import { IoMdClose } from 'react-icons/io';
import { REPORT_FILTERS } from '../../../config/reports';
import FilterFieldRenderer from '../../../components/FilterFieldRenderer/FilterFieldRenderer';
import { AUDIT_TRAIL_FILTERS, DELETED_APPLICATIONS_FILTERS, OFFICER_APPLICATION_FILTERS } from '../../../config/application';
import { ACTIVE_TRANCHE_FILTERS, CLOSED_TRANCHE_FILTERS, PENDING_APPROVAL_TRANCHE_FILTERS, PENDING_DISBURSAL_TRANCHE_FILTERS } from '../../../config/tranche';

const FilterModal = ({ reportTitle, filters, setFilters, resetFilters, handleApplyFilters, onClose, moduleName, dynamicOptions = {}, fields: fieldsOverride, storageKey }) => {
    // An explicit `fields` list always wins, so a screen whose filter keys don't
    // match REPORT_FILTERS can supply its own definitions.
    const fields = Array.isArray(fieldsOverride) && fieldsOverride.length ? fieldsOverride :
        moduleName === "Officer Applications" ? OFFICER_APPLICATION_FILTERS || [] :
            moduleName === "Audit Log" ? AUDIT_TRAIL_FILTERS || [] :
                moduleName === "Active Tranche" ? ACTIVE_TRANCHE_FILTERS || [] :
                    moduleName === "Closed Tranche" ? CLOSED_TRANCHE_FILTERS || [] :
                        moduleName === "Pending for Disbursal Tranche" ? PENDING_DISBURSAL_TRANCHE_FILTERS || [] :
                            moduleName === "Pending for Approval Tranche" ? PENDING_APPROVAL_TRANCHE_FILTERS || [] :
                                moduleName === "Deleted Applications" ? DELETED_APPLICATIONS_FILTERS || [] :
                                    (REPORT_FILTERS[reportTitle] || []);

    const [activeTabKey, setActiveTabKey] = useState(fields[0]?.key || '');
    const [dateError, setDateError] = useState('');

    // Filter edits are staged here and only pushed to the parent (which
    // triggers the fetch) when "Apply Filters" is clicked - not on every
    // keystroke/selection. The modal is mounted fresh each time it opens
    // (screens render it behind `showFilterModal &&`), so seeding from the
    // `filters` prop here always picks up whatever was last applied.
    const [localFilters, setLocalFilters] = useState(filters);

    const persistFilters = (updatedFilters) => {
        // An explicit storageKey keeps screens that share a reportTitle from
        // overwriting each other's saved filters.
        if (storageKey) {
            sessionStorage.setItem(storageKey, JSON.stringify(updatedFilters));
            return;
        }

        sessionStorage.setItem(
            reportTitle === "leads" ? "leadFilters" :
                reportTitle === "applications" ? "applicationFilters" :
                    reportTitle === "limit-sanction" ? "limitSanctionFilters" :
                        reportTitle === "tranche" ? "trancheFilters" :
                            reportTitle === "supplier" ? "supplierFilters" :
                                reportTitle === "demand" ? "demandFilters" :
                                    reportTitle === "active-customer" ? "activeCustomerFilters" :
                                        reportTitle === "repayment" ? "rpsFilters" :
                                            reportTitle === "payment-transaction" ? "paymentTransactionFilters" :
                                                reportTitle === "query-management" ? "queryManagementFilters" :
                                                    reportTitle === "branch-applications" ? "branchApplicationFilters" : "filters"
            , JSON.stringify(updatedFilters));

        sessionStorage.setItem(
            moduleName === "Audit Log" ? "auditTrailFilters" :
                moduleName === "Active Tranche" ? "activeTrancheFilters" :
                    moduleName === "Closed Tranche" ? "closedTrancheFilters" :
                        moduleName === "Pending for Disbursal Tranche" ? "pendingDisbursalTrancheFilters" :
                            moduleName === "Pending for Approval Tranche" ? "pendingApprovalTrancheFilters" :
                                moduleName === "Deleted Applications" ? "deletedApplicationFilters" : "filters",
            JSON.stringify(updatedFilters)
        );
    };

    const handleChange = (key, value) => {
        if (dateError) setDateError('');
        let updatedFilters = {
            ...localFilters,
            [key]: value
        };

        // 🔥 RESET application stage when parent stage changes & sync parent_stage_name with current_application_bucket
        if (key === "current_application_bucket" || key === "parent_stage_name") {
            updatedFilters.application_stage_name = "";
            updatedFilters.current_application_bucket = value;
            updatedFilters.parent_stage_name = value;
        }

        // 🔥 Sync alias keys for leads filters
        // 🔥 Sync alias keys for leads filters
        if (key === "lead_name" || key === "customer_name") {
            updatedFilters.lead_name = value;
            updatedFilters.customer_name = value;
        }
        if (key === "branch" || key === "branch_name") {
            updatedFilters.branch = value;
            updatedFilters.branch_name = value;
        }
        if (key === "branch_code") {
            updatedFilters.branch_code = value;
            // updatedFilters.ro_id = [];   // FR-02: branch change resets RO selection
            // updatedFilters.ro_id = "";

            // Only reset RO selection if branch actually changed AND previous branch was different
            const prevBranch = localFilters?.branch_code || [];
            const nextBranch = value || [];
            const branchChanged =
                prevBranch.length !== nextBranch.length ||
                [...prevBranch].sort().join(',') !== [...nextBranch].sort().join(',');
            if (branchChanged && localFilters?.ro_id?.length > 0) {
                updatedFilters.ro_id = [];
            }
        }
        if (key === "creation_start_date" || key === "created_from") {
            updatedFilters.creation_start_date = value;
            updatedFilters.created_from = value;
        }
        if (key === "creation_end_date" || key === "created_to") {
            updatedFilters.creation_end_date = value;
            updatedFilters.created_to = value;
        }

        // Staged only - the parent's live filter state (and the fetch it
        // triggers) isn't touched until Apply Filters is clicked.
        setLocalFilters(updatedFilters);
    };

    const checkFilterHasValue = (key, field) => {
        let val = localFilters[key];
        if (field?.type === 'date_range') {
            val = filters[field.startKey || 'start_date'] || filters[field.endKey || 'end_date'];
        }
        if (key === "current_application_bucket" || key === "parent_stage_name") {
            val = localFilters.current_application_bucket || localFilters.parent_stage_name;
        }
        if (key === "lead_name" || key === "customer_name") {
            val = localFilters.lead_name || localFilters.customer_name;
        }
        if (key === "branch" || key === "branch_name") {
            val = localFilters.branch || localFilters.branch_name;
        }
        if (key === "creation_start_date" || key === "created_from") {
            val = localFilters.creation_start_date || localFilters.created_from;
        }
        if (key === "creation_end_date" || key === "created_to") {
            val = localFilters.creation_end_date || localFilters.created_to;
        }
        if (Array.isArray(val)) return val.length > 0;
        return val !== '' && val !== null && val !== undefined;
    };

    const activeField = fields.find(f => f.key === (activeTabKey || fields[0]?.key)) || fields[0];

    const getTitleText = () => {
        if (moduleName) return `Filter ${moduleName}`;
        if (reportTitle === "applications" || reportTitle === "branch-applications") return "Filter Applications";
        if (reportTitle === "leads") return "Filter Leads";
        if (reportTitle === "limit-sanction") return "Filter Limit Sanction";
        if (reportTitle === "demand") return "Filter Demand";
        if (reportTitle === "supplier") return "Filter Supplier";
        if (reportTitle === "tranche") return "Filter Tranche";
        if (reportTitle === "repayment") return "Filter Repayment";
        if (reportTitle === "active-customer") return "Filter Active Customer";
        if (reportTitle === "payment-transaction") return "Filter Payment Transactions";
        if (reportTitle === "query-management") return "Filter Queries";
        return "Advanced Filters";
    };

    const getFieldOptions = (fieldKey) => {
        if (dynamicOptions[fieldKey]) return dynamicOptions[fieldKey];
        if (fieldKey === "current_application_bucket" && dynamicOptions["parent_stage_name"]) {
            return dynamicOptions["parent_stage_name"];
        }
        if (fieldKey === "parent_stage_name" && dynamicOptions["current_application_bucket"]) {
            return dynamicOptions["current_application_bucket"];
        }
        if (fieldKey === "application_stage_name" && dynamicOptions["stage"]) {
            return dynamicOptions["stage"];
        }
        if (fieldKey === "stage" && dynamicOptions["application_stage_name"]) {
            return dynamicOptions["application_stage_name"];
        }
        return activeField?.options || [];
    };

    const getFieldValue = (fieldKey) => {
        if (fieldKey === "current_application_bucket" || fieldKey === "parent_stage_name") {
            return localFilters.current_application_bucket || localFilters.parent_stage_name || "";
        }
        return localFilters[fieldKey];
    };

    return ReactDOM.createPortal(
        <>
            <div className="filterPopoverBackdrop" onClick={onClose} />
            <div className="filterPopoverPanel" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="filterModalHeader">
                    <h2>{getTitleText()}</h2>
                    <button onClick={onClose} className="filterModalCloseBtn">
                        <IoMdClose size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="filterModalBody">
                    <div className="filterDrawerLayout">
                        {/* Left Sidebar Menu */}
                        <div className="filterNavSidebar">
                            {fields.map((field) => {
                                // const hasVal = checkFilterHasValue(field.key);
                                const hasVal = checkFilterHasValue(field.key, field);
                                const isActive = (activeTabKey || fields[0]?.key) === field.key;
                                return (
                                    <button
                                        key={field.key}
                                        className={`filterNavItem ${isActive ? 'active' : ''}`}
                                        onClick={() => setActiveTabKey(field.key)}
                                    >
                                        <span className="filterNavLabel">{field.label}</span>
                                        {hasVal && <span className="filterNavBadge" />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Right Content Panel */}
                        <div className="filterContentPanel">
                            {activeField && (
                                <div className="filterPanelSection">
                                    <div className="filterPanelTitle">{activeField.label}</div>
                                    <FilterFieldRenderer
                                        field={{
                                            ...activeField,
                                            options: getFieldOptions(activeField.key)
                                        }}
                                        value={getFieldValue(activeField.key)}
                                        onChange={handleChange}
                                        filters={localFilters}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Date Pair Validation Error Banner */}
                {dateError && (
                    <div className="filterModalDateError">
                        {dateError}
                    </div>
                )}

                {/* Modal Footer */}
                <div className="filterModalFooter">
                    <button onClick={() => {
                        setDateError('');
                        // resetFilters (consumer-supplied) clears the parent's live
                        // filter state and re-fetches with the dashboard's initial
                        // filters. It returns that same object so the modal's own
                        // staged localFilters mirrors it - otherwise the fields
                        // would keep showing the pre-clear values if the modal
                        // stays open, and a later Apply would re-push the stale draft.
                        const clearedFilters = resetFilters();
                        if (clearedFilters && typeof clearedFilters === 'object') {
                            setLocalFilters(clearedFilters);
                        }
                    }} className="clearAllBtn">
                        Clear Filters
                    </button>
                    <button onClick={() => {
                        const startDate = localFilters?.from_date || localFilters?.start_date || localFilters?.creation_start_date || localFilters?.created_from;
                        const endDate = localFilters?.to_date || localFilters?.end_date || localFilters?.creation_end_date || localFilters?.created_to;

                        if (startDate && !endDate) {
                            const msg = "Please select Requested End Date as well.";
                            setDateError(msg);
                            toast.error(msg);
                            return;
                        }
                        if (!startDate && endDate) {
                            const msg = "Please select Requested Start Date as well.";
                            setDateError(msg);
                            toast.error(msg);
                            return;
                        }
                        setDateError('');
                        setFilters(localFilters);
                        persistFilters(localFilters);
                        handleApplyFilters(localFilters);
                    }} className="applyFiltersBtn">
                        Apply Filters
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

export default FilterModal;